import { i18nTime } from '@affine/i18n';
import {
  AliasIcon as LitAliasIcon,
  BlockLinkIcon as LitBlockLinkIcon,
  EdgelessIcon as LitEdgelessIcon,
  JournalIcon as LitJournalIcon,
  LinkedEdgelessIcon as LitLinkedEdgelessIcon,
  LinkedPageIcon as LitLinkedPageIcon,
  PageIcon as LitPageIcon,
  TemplateIcon as LitTemplateIcon,
  WebIcon as LitWebIcon,
} from '@blocksuite/icons/lit';
import {
  AliasIcon,
  BlockLinkIcon,
  EdgelessIcon,
  JournalIcon,
  LinkedEdgelessIcon,
  LinkedPageIcon,
  PageIcon,
  TemplateIcon,
  WebIcon,
} from '@blocksuite/icons/rc';
import { LiveData, Service } from '@toeverything/infra';
import dayjs, { type Dayjs } from 'dayjs';
import type { TemplateResult } from 'lit';
import { html } from 'lit';
import { createElement, type SVGProps, useId } from 'react';

import type { DocRecord, DocsService } from '../../doc';
import type { ExplorerIconService } from '../../explorer-icon/services/explorer-icon';
import type { I18nService } from '../../i18n';
import type { JournalService } from '../../journal';
import { StreamLainCustomAppIcon } from '../../streamlain/components/custom-app-icon';
import {
  isStreamLainCustomAppRootDocKind,
  STREAMLAIN_DOC_KIND_CUSTOM_PROPERTY_ID,
} from '../../streamlain/constants/custom-app-doc';
import type { TemplateDocService } from '../../template-doc';
import { getDocIconComponent, getDocIconComponentLit } from './icon';

type IconType = 'rc' | 'lit';
interface DocDisplayIconOptions<T extends IconType> {
  type?: T;
  /**
   * Override the mode detected inside the hook:
   * by default, it will use the `primaryMode$` of the doc.
   */
  mode?: 'edgeless' | 'page';
  title?: string; // title alias
  reference?: boolean;
  referenceToNode?: boolean;
  /**
   * @default true
   */
  enableEmojiIcon?: boolean;
}
interface DocDisplayTitleOptions {
  title?: string; // title alias
  reference?: boolean;
  /**
   * @default true
   */
  enableEmojiIcon?: boolean;
}

const rcIcons = {
  AliasIcon,
  BlockLinkIcon,
  EdgelessIcon,
  LinkedEdgelessIcon,
  LinkedPageIcon,
  PageIcon,
  JournalIcon,
  TemplateIcon,
  CustomAppRootIcon: StreamLainCustomAppIcon,
  WebIcon,
};
const litIcons = {
  AliasIcon: LitAliasIcon,
  BlockLinkIcon: LitBlockLinkIcon,
  EdgelessIcon: LitEdgelessIcon,
  LinkedEdgelessIcon: LitLinkedEdgelessIcon,
  LinkedPageIcon: LitLinkedPageIcon,
  PageIcon: LitPageIcon,
  JournalIcon: LitJournalIcon,
  TemplateIcon: LitTemplateIcon,
  CustomAppRootIcon: LitWebIcon,
  WebIcon: LitWebIcon,
};
const icons = { rc: rcIcons, lit: litIcons } as {
  rc: Record<keyof typeof rcIcons, any>;
  lit: Record<keyof typeof litIcons, any>;
};

const JOURNAL_ICON_PATH =
  'M16.25 4.5H18a.5.5 0 0 1 .5.5v14a.5.5 0 0 1-.5.5H6a.5.5 0 0 1-.5-.5V5a.5.5 0 0 1 .5-.5h4.25V11a.75.75 0 0 0 1.166.624l1.834-1.223 1.834 1.223A.75.75 0 0 0 16.25 11zM4 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm7.75 4.599V4.75h3v4.849l-1.084-.723a.75.75 0 0 0-.832 0z';
const JOURNAL_FUTURE_BADGE_PATH =
  'M16.2 15.85L18 17.65l-1.8 1.8M18.7 15.85l1.8 1.8-1.8 1.8';
const JOURNAL_PAST_BADGE_PATH =
  'M18 15.85l-1.8 1.8 1.8 1.8M20.5 15.85l-1.8 1.8 1.8 1.8';
const JOURNAL_STATUS_BADGE_CENTER = 18.3;
const JOURNAL_STATUS_BADGE_RADIUS = 4.75;
const JOURNAL_STATUS_BADGE_CUTOUT_RADIUS = 5.35;

function createJournalStatusIcon(badgePath: string): typeof JournalIcon {
  return function JournalStatusIcon({
    height = '1em',
    style,
    width = '1em',
    ...props
  }: SVGProps<SVGSVGElement>) {
    const rawMaskId = useId();
    const maskId = `journal-status-${rawMaskId.replace(/:/g, '')}`;

    return createElement(
      'svg',
      {
        ...props,
        fill: 'none',
        height,
        style: {
          userSelect: 'none',
          flexShrink: 0,
          ...style,
        },
        viewBox: '0 0 24 24',
        width,
        xmlns: 'http://www.w3.org/2000/svg',
      },
      createElement(
        'defs',
        {},
        createElement(
          'mask',
          { id: maskId },
          createElement('rect', {
            fill: 'white',
            height: 24,
            width: 24,
            x: 0,
            y: 0,
          }),
          createElement('circle', {
            cx: JOURNAL_STATUS_BADGE_CENTER,
            cy: JOURNAL_STATUS_BADGE_CENTER,
            fill: 'black',
            r: JOURNAL_STATUS_BADGE_CUTOUT_RADIUS,
          })
        )
      ),
      createElement('path', {
        clipRule: 'evenodd',
        d: JOURNAL_ICON_PATH,
        fill: 'currentColor',
        fillRule: 'evenodd',
        mask: `url(#${maskId})`,
      }),
      createElement('circle', {
        cx: JOURNAL_STATUS_BADGE_CENTER,
        cy: JOURNAL_STATUS_BADGE_CENTER,
        fill: 'none',
        r: JOURNAL_STATUS_BADGE_RADIUS,
        stroke: 'currentColor',
        strokeWidth: 1.35,
      }),
      createElement('path', {
        d: badgePath,
        fill: 'none',
        stroke: 'currentColor',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeWidth: 1.35,
      })
    );
  };
}

function createJournalStatusIconLit(badgePath: string): typeof LitJournalIcon {
  const maskId =
    badgePath === JOURNAL_FUTURE_BADGE_PATH
      ? 'journal-status-future-mask'
      : 'journal-status-past-mask';

  return function JournalStatusIconLit({
    height = '1em',
    style = '',
    width = '1em',
  }: {
    height?: string;
    style?: string;
    width?: string;
  } = {}): TemplateResult<1> {
    return html`
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width=${width}
        height=${height}
        fill="none"
        style=${'user-select:none;flex-shrink:0;' + style}
      >
        <defs>
          <mask id=${maskId}>
            <rect x="0" y="0" width="24" height="24" fill="white"></rect>
            <circle
              cx=${JOURNAL_STATUS_BADGE_CENTER}
              cy=${JOURNAL_STATUS_BADGE_CENTER}
              r=${JOURNAL_STATUS_BADGE_CUTOUT_RADIUS}
              fill="black"
            ></circle>
          </mask>
        </defs>
        <path
          fill="currentColor"
          fill-rule="evenodd"
          d=${JOURNAL_ICON_PATH}
          clip-rule="evenodd"
          mask=${`url(#${maskId})`}
        ></path>
        <circle
          cx=${JOURNAL_STATUS_BADGE_CENTER}
          cy=${JOURNAL_STATUS_BADGE_CENTER}
          r=${JOURNAL_STATUS_BADGE_RADIUS}
          fill="none"
          stroke="currentColor"
          stroke-width="1.35"
        ></circle>
        <path
          d=${badgePath}
          fill="none"
          stroke="currentColor"
          stroke-width="1.35"
          stroke-linecap="round"
          stroke-linejoin="round"
        ></path>
      </svg>
    `;
  };
}

const JournalFutureIcon = createJournalStatusIcon(JOURNAL_FUTURE_BADGE_PATH);
const JournalPastIcon = createJournalStatusIcon(JOURNAL_PAST_BADGE_PATH);
const LitJournalFutureIcon = createJournalStatusIconLit(
  JOURNAL_FUTURE_BADGE_PATH
);
const LitJournalPastIcon = createJournalStatusIconLit(JOURNAL_PAST_BADGE_PATH);

export class DocDisplayMetaService extends Service {
  constructor(
    private readonly journalService: JournalService,
    private readonly docsService: DocsService,
    private readonly i18nService: I18nService,
    private readonly explorerIconService: ExplorerIconService,
    private readonly templateDocService: TemplateDocService
  ) {
    super();
  }

  getJournalIcon(
    journalDate: string | Dayjs,
    options?: DocDisplayIconOptions<'rc'>
  ): typeof JournalIcon;

  getJournalIcon(
    journalDate: string | Dayjs,
    options?: DocDisplayIconOptions<'lit'>
  ): typeof LitJournalIcon;

  getJournalIcon<T extends IconType = 'rc'>(
    journalDate: string | Dayjs,
    options?: DocDisplayIconOptions<T>
  ): T extends 'rc' ? typeof JournalIcon : typeof LitJournalIcon;

  getJournalIcon<T extends IconType = 'rc'>(
    journalDate: string | Dayjs,
    options?: DocDisplayIconOptions<T>
  ) {
    const iconSet = icons[options?.type ?? 'rc'];
    const day = dayjs(journalDate);
    if (day.isBefore(dayjs(), 'day')) {
      return options?.type === 'lit' ? LitJournalPastIcon : JournalPastIcon;
    }
    if (day.isAfter(dayjs(), 'day')) {
      return options?.type === 'lit' ? LitJournalFutureIcon : JournalFutureIcon;
    }
    return iconSet.JournalIcon;
  }

  icon$<T extends IconType = 'rc'>(
    docId: string,
    options?: DocDisplayIconOptions<T>
  ) {
    const iconSet = icons[options?.type ?? 'rc'];

    return LiveData.computed(get => {
      const enableEmojiIcon = options?.enableEmojiIcon !== false;
      const referenced = !!options?.reference;
      const titleAlias = referenced ? options?.title : undefined;
      // const originalTitle = doc ? get(doc.title$) : '';
      // link to journal doc
      const journalDateString = get(this.journalService.journalDate$(docId));
      const journalIcon = journalDateString
        ? this.getJournalIcon(journalDateString, options)
        : undefined;
      // const journalTitle = journalDateString
      //   ? i18nTime(journalDateString, { absolute: { accuracy: 'day' } })
      //   : undefined;
      // const title = titleAlias ?? journalTitle ?? originalTitle;
      // Always show page icon in sidebar/lists — primaryMode is irrelevant for navigation
      const finalMode = options?.mode ?? 'page';
      const referenceToNode = !!(referenced && options.referenceToNode);
      const doc = get(this.docsService.list.doc$(docId));
      const docKind = doc
        ? get(doc.customProperty$(STREAMLAIN_DOC_KIND_CUSTOM_PROPERTY_ID))
        : undefined;
      const isCustomAppRootDoc = isStreamLainCustomAppRootDocKind(docKind);
      const isTemplateDoc = get(
        this.templateDocService.list.isTemplate$(docId)
      );

      // emoji title
      if (enableEmojiIcon) {
        // const { emoji } = extractEmojiIcon(title);
        // if (emoji) return () => emoji;
        const icon = get(this.explorerIconService.icon$('doc', docId))?.icon;
        if (icon) {
          return options?.type === 'lit'
            ? getDocIconComponentLit(icon)
            : getDocIconComponent(icon);
        }
      }

      // title alias
      if (titleAlias) return iconSet.AliasIcon;

      if (journalIcon) return journalIcon;

      if (!referenced && isCustomAppRootDoc) return iconSet.CustomAppRootIcon;

      if (!referenced && isTemplateDoc) return iconSet.TemplateIcon;

      // link to specified block
      if (referenceToNode) return iconSet.BlockLinkIcon;

      // link to regular doc (reference)
      if (options?.reference) {
        return finalMode === 'edgeless'
          ? iconSet.LinkedEdgelessIcon
          : iconSet.LinkedPageIcon;
      }

      // default icon
      return finalMode === 'edgeless' ? iconSet.EdgelessIcon : iconSet.PageIcon;
    });
  }

  title$(docId: string, options?: DocDisplayTitleOptions) {
    return LiveData.computed(get => {
      const lng = get(this.i18nService.i18n.currentLanguageKey$);
      const doc = get(this.docsService.list.doc$(docId));
      const referenced = !!options?.reference;
      const titleAlias = referenced ? options?.title : undefined;
      const originalTitle = doc ? get(doc.title$) : '';
      // journal title
      const journalDateString = get(this.journalService.journalDate$(docId));
      const journalTitle = journalDateString
        ? i18nTime(journalDateString, { absolute: { accuracy: 'day' } })
        : undefined;
      // const title = titleAlias ?? journalTitle ?? originalTitle;

      // emoji title
      // if (enableEmojiIcon && title) {
      //   // When the title has only one emoji character,
      //   // if it's a journal document, the date should be displayed.
      //   const journalDateString = get(this.journalService.journalDate$(docId));
      //   if (journalDateString) {
      //     return i18nTime(journalDateString, { absolute: { accuracy: 'day' } });
      //   }
      // }

      // title alias
      if (titleAlias) return titleAlias;

      if (journalTitle) return journalTitle;

      // doc not found
      if (!doc) {
        return this.i18nService.i18n.i18next.t(
          'com.streamlain.notFoundPage.title',
          { lng }
        );
      }

      // original title
      if (originalTitle) return originalTitle;

      // empty title
      return this.i18nService.i18n.i18next.t('Untitled', { lng });
    });
  }

  getDocDisplayMeta(docRecord: DocRecord) {
    return {
      title: this.title$(docRecord.id).value,
      icon: this.icon$(docRecord.id).value,
      updatedDate: docRecord.meta$.value.updatedDate,
    };
  }
}
